import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const SESSION_KEY = 'michinnyon-welcome-session';
const DEADLINE_KEY = 'michinnyon-welcome-deadline';
const PENDING_KEY = 'michinnyon-welcome-pending';
const DISMISSED_KEY = 'michinnyon-welcome-dismissed';
const SUBSCRIBED_KEY = 'michinnyon-welcome-subscribed';
const VISIT_KEY = 'michinnyon-cart-reminder-visit';
const VISIT_TIMEOUT = 30 * 60 * 1000;
const PURPOSES = ['preferences', 'analytics', 'marketing'];
const PRIVACY_METHODS = ['currentVisitorConsent', 'shouldShowBanner', 'setTrackingConsent', 'preferencesProcessingAllowed'];
const WELCOME_STATES = new Map();

/** Native Shopify consent and newsletter forms, presented in the store's visual style. */
class ConsentWelcome extends Component {
  #abort;
  #privacy;
  #timer;
  #saveTimer;
  #finishConsent;
  #returnFocus;
  #seen = false;
  #busy = false;
  #preview = '';
  #cookieStage = 'intro';
  #cookieOpen = false;
  #cookiePreviewShown = false;
  #cookieDraft;
  #welcomeState;
  #welcomeAt = 0;
  #pendingActions = new Set();
  #pointers = new Set();
  #scrollingUntil = 0;
  #subscribed = false;
  #visit;
  #reminderRequest;
  #reminderLoading = false;
  #reminderItems;
  #reminderOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#disconnect();
    this.#connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnect();
    clearTimeout(this.#saveTimer);
    this.#finishConsent = undefined;
    this.#busy = false;
  }

  #disconnect() {
    this.#abort?.abort();
    this.#reminderRequest?.abort();
    this.#reminderRequest = undefined;
    this.#reminderLoading = false;
    this.#privacy = undefined;
    clearTimeout(this.#timer);
    unlockScroll(this);
    document.documentElement.classList.remove('mw-consent-ready');
  }

  #connect() {
    this.#welcomeState = WELCOME_STATES.get(this.dataset.sectionId) ?? { stage: 'offer', open: false, previewShown: false, resultHandled: false, email: '' };
    WELCOME_STATES.set(this.dataset.sectionId, this.#welcomeState);
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const params = new URLSearchParams(location.search);
    this.#preview = this.dataset.preview && this.dataset.preview !== 'none' ? this.dataset.preview : '';
    if (!this.#preview && params.get('preview_privacy_banner') === '1') this.#preview = 'cookies';
    if (!this.#preview && params.get('michinnyon_preview') === 'welcome') this.#preview = 'welcome';
    if (this.#preview !== 'cookies') this.#cookiePreviewShown = false;
    if (this.#preview !== 'welcome') this.#welcomeState.previewShown = false;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onCookieChange, { signal });
    this.addEventListener('input', this.#onWelcomeInput, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener('cancel', this.#onCancel, { signal });
      dialog.addEventListener('close', this.#onClosed, { signal });
    }
    document.addEventListener('click', this.#onPreferencesLink, { capture: true, signal });
    document.addEventListener('visitorConsentCollected', this.#onConsentChanged, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onPendingAction, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onPendingAction, { signal });
    document.addEventListener('pointerdown', this.#onPointerDown, { capture: true, passive: true, signal });
    document.addEventListener('pointerup', this.#onPointerUp, { capture: true, passive: true, signal });
    document.addEventListener('pointercancel', this.#onPointerUp, { capture: true, passive: true, signal });
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true, signal });
    window.addEventListener('blur', this.#onWindowBlur, { signal });
    window.addEventListener('pagehide', this.#touchVisit, { signal });
    document.addEventListener('visibilitychange', this.#onVisibilityChanged, { signal });
    window.addEventListener('pageshow', this.#onVisibilityChanged, { signal });
    window.addEventListener('storage', this.#onSubscriptionChanged, { signal });

    const pending = this.#session(PENDING_KEY) === '1';
    const welcomeForm = this.querySelector('.mw-welcome__form');
    const returningToWelcome = pending || (welcomeForm && location.hash === `#${welcomeForm.id}`);
    const welcomeSuccess = this.querySelector('[data-welcome-success]');
    const welcomeError = this.querySelector('[data-welcome-error]');
    const newsletterSuccess = this.ownerDocument?.querySelector('.email-signup__form [id^="Email-signup__message-success-"]');
    if ((returningToWelcome && welcomeSuccess) || newsletterSuccess) this.#rememberSubscriber();
    if (!this.#welcomeState.resultHandled && returningToWelcome && (welcomeSuccess || welcomeError)) {
      this.#welcomeState.resultHandled = true;
      this.#session(PENDING_KEY, '');
      if (welcomeForm && location.hash === `#${welcomeForm.id}`) {
        const returnUrl = new URL(location.href);
        returnUrl.hash = '';
        window.history.replaceState(window.history.state, '', returnUrl);
      }
      this.#openWelcome(welcomeSuccess ? 'success' : 'form');
    }

    const ready = () => {
      const privacy = window.Shopify?.customerPrivacy;
      if (signal.aborted || !PRIVACY_METHODS.every((method) => typeof privacy?.[method] === 'function')) return false;
      this.#privacy = privacy;
      this.#clearUnconsentedStorage();
      this.#startVisit();
      if (this.#cookieOpen) this.#openCookies(this.#cookieStage);
      else if (this.#welcomeState.open) this.#openWelcome(this.#welcomeState.stage);
      else if (this.#reminderOpen && this.#trackingAccepted()) this.#show(this.refs.cartReminderDialog);
      else if (this.#preview === 'cookies' && !this.#cookiePreviewShown && (this.dataset.preview === 'cookies' || !this.#hasConsentChoice())) {
        this.#cookiePreviewShown = true;
        this.#openCookies();
      }
      else if (this.#preview === 'welcome') {
        if (!this.#welcomeState.previewShown && (this.dataset.preview === 'welcome' || !this.#session(SESSION_KEY))) {
          this.#welcomeState.previewShown = true;
          this.#openWelcome('offer');
        }
      }
      else if (!window.Shopify?.designMode) {
        if (this.#needsConsent() && !this.refs.welcomeDialog?.open) this.#openCookies();
        else this.#scheduleWelcome();
      }
      if (this.refs.cookieDialog && document.documentElement.dataset.mwConsentOwner !== 'native') {
        document.documentElement.classList.add('mw-consent-ready');
        document.documentElement.dataset.mwConsentOwner = 'custom';
        document.documentElement.classList.remove('mw-consent-pending');
      }
      return true;
    };
    if (!ready()) {
      if (typeof window.Shopify?.loadFeatures === 'function') {
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (error || !ready()) this.#useNativeConsent();
          });
        } catch { this.#useNativeConsent(); }
      } else this.#useNativeConsent();
    }
  }

  #useNativeConsent() {
    if (this.#abort.signal.aborted || !this.refs.cookieDialog || document.documentElement.dataset.mwConsentOwner === 'custom') return;
    document.documentElement.dataset.mwConsentOwner = 'native';
    document.documentElement.classList.remove('mw-consent-pending');
  }

  #hasConsentChoice() {
    const consent = this.#privacy?.currentVisitorConsent();
    return consent && PURPOSES.some((purpose) => consent[purpose] === 'yes' || consent[purpose] === 'no');
  }

  #needsConsent() {
    return !this.#privacy || (this.#privacy.shouldShowBanner() && !this.#hasConsentChoice());
  }

  #show(dialog) {
    if (!dialog) return;
    if (!dialog.open) {
      this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    }
    lockScroll(this);
  }

  #openCookies(stage = 'intro') {
    const dialog = this.refs.cookieDialog;
    if (!dialog || !this.#privacy || document.documentElement.dataset.mwConsentOwner === 'native') return;
    if (!this.#cookieOpen && !this.#preview && !window.Shopify?.designMode) {
      // A new cookie visit starts its five seconds on close, never behind the consent screen.
      this.#welcomeAt = 0;
      this.#session(DEADLINE_KEY, '');
    }
    this.#cookieStage = stage;
    this.#cookieOpen = true;
    this.#closeReminder();
    clearTimeout(this.#timer);
    if (this.refs.welcomeDialog?.open) {
      this.#welcomeState.open = false;
      this.refs.welcomeDialog.close();
    }
    const consent = this.#privacy.currentVisitorConsent();
    if (stage === 'preferences' && !this.#cookieDraft) {
      this.#cookieDraft = Object.fromEntries(PURPOSES.map((purpose) => [purpose, consent[purpose] !== 'no']));
    }
    for (const purpose of PURPOSES) {
      const input = dialog.querySelector(`input[name="${purpose}"]`);
      if (input) input.checked = this.#cookieDraft?.[purpose] ?? (consent[purpose] !== 'no');
    }
    for (const panel of dialog.querySelectorAll('[data-cookie-stage]')) {
      panel.hidden = panel.dataset.cookieStage !== stage;
    }
    dialog.querySelectorAll('button').forEach((button) => { button.disabled = this.#busy; });
    dialog.classList.toggle('mw-consent--intro', stage === 'intro');
    dialog.scrollTop = 0;
    const title = dialog.querySelector(`[data-cookie-stage="${stage}"] h2`);
    if (title) dialog.setAttribute('aria-labelledby', title.id);
    if (stage === 'intro') dialog.setAttribute('aria-describedby', `CookieIntroText-${this.dataset.sectionId}`);
    else dialog.removeAttribute('aria-describedby');
    this.#show(dialog);
    title?.focus({ preventScroll: true });
  }

  #openWelcome(stage) {
    const dialog = this.refs.welcomeDialog;
    if (!dialog || this.refs.cookieDialog?.open || this.refs.cartReminderDialog?.open) return;
    this.#welcomeState.stage = stage;
    this.#welcomeState.open = true;
    if (this.#preview === 'welcome') this.#welcomeState.previewShown = true;
    this.#seen = true;
    this.#session(SESSION_KEY, '1');
    this.#session(DEADLINE_KEY, '');
    clearTimeout(this.#timer);
    for (const panel of dialog.querySelectorAll('[data-welcome-stage]')) {
      panel.hidden = panel.dataset.welcomeStage !== stage;
    }
    const current = dialog.querySelector(`[data-welcome-stage="${stage}"]`);
    const title = current?.querySelector('h2');
    if (title) {
      title.id = `Welcome-${this.dataset.sectionId}-${stage}`;
      dialog.setAttribute('aria-labelledby', title.id);
    }
    this.#show(dialog);
    if (stage === 'form') {
      const email = current?.querySelector('input[type="email"]');
      if (email) {
        if (!email.value) email.value = this.#welcomeState.email;
        email.focus({ preventScroll: true });
      }
    }
    else title?.focus({ preventScroll: true });
  }

  #scheduleWelcome = () => {
    clearTimeout(this.#timer);
    if (!this.isConnected || this.#preview || window.Shopify?.designMode || this.#cookieOpen || this.refs.cookieDialog?.open || this.#needsConsent()) return;
    if (this.#scheduleReminder()) return;
    if (!this.refs.welcomeDialog || this.#isSubscriber() || this.#visit?.reminded || this.#seen || this.#session(SESSION_KEY)) return;
    if (this.#preferencesAllowed()) {
      try {
        const dismissed = Number(localStorage.getItem(DISMISSED_KEY));
        if (dismissed && Date.now() - dismissed < Number(this.dataset.repeatDays || 3) * 86400000) return;
      } catch { /* Storage may be unavailable in private browsing. */ }
    }
    if (!this.#welcomeAt) {
      const saved = Number(this.#session(DEADLINE_KEY));
      this.#welcomeAt = Number.isFinite(saved) && saved > 0 ? saved : Date.now() + Math.max(1, Number(this.dataset.delay || 5)) * 1000;
      // Keep the same deadline through full-page navigation as well as section updates.
      this.#session(DEADLINE_KEY, String(this.#welcomeAt));
    }
    const remaining = this.#welcomeAt - Date.now();
    if (remaining > 0 || this.#welcomeBlocked()) {
      // Once due, wait only for the current interaction; do not restart the five-second delay.
      this.#timer = setTimeout(this.#scheduleWelcome, remaining > 0 ? remaining : 250);
      return;
    }
    this.#seen = true;
    this.#session(SESSION_KEY, '1');
    this.#session(DEADLINE_KEY, '');
    this.#openWelcome('offer');
  };

  #welcomeBlocked() {
    if (document.hidden || this.#busy || this.dataset.pageType === 'cart' || this.#pendingActions.size || this.#pointers.size || Date.now() < this.#scrollingUntil) return true;
    if (document.querySelector('dialog[open], theme-drawer[open], header-drawer details[open], quick-add-component[data-inline-open], [aria-busy="true"]')) return true;
    return document.activeElement instanceof Element && document.activeElement.matches('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):not([type="reset"]), textarea, select, [contenteditable="true"]');
  }

  #onPendingAction = (event) => {
    if (event.type === StandardEvents.cartLinesUpdate && this.#visit) {
      this.#visit.eligible = false;
      this.#saveVisit();
      this.#reminderRequest?.abort();
      this.#reminderItems = undefined;
      this.#closeReminder();
    }
    const pending = event.promise;
    if (!pending || typeof pending.then !== 'function') return;
    this.#pendingActions.add(pending);
    const settled = () => { this.#pendingActions.delete(pending); };
    // Native variant/cart promises include failures, so an unsuccessful request also releases the gate.
    pending.then(settled, settled);
  };

  #onPointerDown = (event) => { this.#pointers.add(event.pointerId); this.#touchVisit(); };
  #onPointerUp = (event) => { this.#pointers.delete(event.pointerId); };
  #onWindowBlur = () => { this.#pointers.clear(); };
  #onScroll = () => { this.#scrollingUntil = Date.now() + 200; this.#touchVisit(); };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-cart-reminder-close]') || event.target === this.refs.cartReminderDialog) {
      this.#closeReminder();
      return;
    }
    const action = event.target.closest('[data-consent-action]');
    if (action) {
      event.preventDefault();
      event.stopPropagation();
      if (action.dataset.consentAction === 'preferences') this.#openCookies('preferences');
      else if (action.dataset.consentAction === 'deny') this.#denyOptionalCookies();
      else this.#saveConsent(action.dataset.consentAction);
      return;
    }
    if (event.target.closest('[data-welcome-yes]')) this.#openWelcome('form');
    else if (event.target.closest('[data-welcome-close]')) this.#dismissWelcome();
  };

  #denyOptionalCookies() {
    if (this.#busy || !this.refs.cookieDialog) return;
    this.#cookieDraft = Object.fromEntries(PURPOSES.map((purpose) => [purpose, false]));
    for (const purpose of PURPOSES) {
      const input = this.refs.cookieDialog.querySelector(`input[name="${purpose}"]`);
      if (input) input.checked = false;
    }
  }

  #onCookieChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !PURPOSES.includes(event.target.name)) return;
    this.#cookieDraft = Object.fromEntries(PURPOSES.map((purpose) => [purpose, this.refs.cookieDialog.querySelector(`input[name="${purpose}"]`).checked]));
  };

  #onPreferencesLink = (event) => {
    if (!(event.target instanceof Element) || !this.refs.cookieDialog || !this.#privacy || document.documentElement.dataset.mwConsentOwner === 'native') return;
    if (event.target.closest('a[href="#shopifyReshowConsentBanner"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#openCookies('preferences');
    }
  };

  #onWelcomeInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('.mw-welcome__form input[type="email"]')) {
      this.#welcomeState.email = event.target.value;
    }
  };

  #onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement && event.target.matches('.mw-welcome__form')) {
      this.#welcomeState.resultHandled = false;
      this.#seen = true;
      this.#session(SESSION_KEY, '1');
      this.#session(PENDING_KEY, '1');
      // Keep Shopify's form submission, spam protection and subscriber trigger intact.
    }
  };

  #onCancel = (event) => {
    event.preventDefault();
    if (event.target === this.refs.cartReminderDialog) this.#closeReminder();
    else if (event.target === this.refs.welcomeDialog) this.#dismissWelcome();
    else if (!this.#busy) {
      this.#cookieOpen = false;
      this.#cookieDraft = undefined;
      event.target.close(); // Escape does not grant consent.
    }
  };

  #onClosed = (event) => {
    if (event.target === this.refs.cartReminderDialog && !event.target.open) this.#reminderOpen = false;
    if (event.target === this.refs.cookieDialog) {
      if (this.refs.cookieDialog.open) return;
      this.#cookieOpen = false;
      this.#cookieDraft = undefined;
      this.#scheduleWelcome();
    }
    if (event.target === this.refs.welcomeDialog) {
      if (this.refs.welcomeDialog.open) return;
      this.#welcomeState.open = false;
    }
    if (this.querySelector('dialog[open]')) return;
    unlockScroll(this);
    if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
  };

  #dismissWelcome() {
    this.#welcomeState.open = false;
    this.#welcomeState.email = '';
    this.#session(PENDING_KEY, '');
    this.#seen = true;
    this.#session(SESSION_KEY, '1');
    if (!this.#preview && this.#preferencesAllowed()) {
      try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* Optional storage. */ }
    }
    this.refs.welcomeDialog?.close();
  }

  #saveConsent(action) {
    if (this.#busy || !this.#privacy || !this.refs.cookieDialog) return;
    const dialog = this.refs.cookieDialog;
    const consent = Object.fromEntries(PURPOSES.map((purpose) => [purpose, action === 'save' ? dialog.querySelector(`input[name="${purpose}"]`).checked : action === 'accept']));
    const error = dialog.querySelector('[data-cookie-stage]:not([hidden]) .mw-consent__error');
    error.hidden = true;
    this.#busy = true;
    dialog.querySelectorAll('button').forEach((button) => { button.disabled = true; });
    const done = (result) => {
      if (this.#finishConsent !== done) return;
      this.#finishConsent = undefined;
      clearTimeout(this.#saveTimer);
      this.#busy = false;
      const currentDialog = this.refs.cookieDialog;
      currentDialog?.querySelectorAll('button').forEach((button) => { button.disabled = false; });
      if (result?.error) {
        const currentError = currentDialog?.querySelector('[data-cookie-stage]:not([hidden]) .mw-consent__error');
        if (currentError) currentError.hidden = false;
        return;
      }
      this.#clearUnconsentedStorage();
      this.#closeCookies();
      // A section refresh may have replaced the dialog during Shopify's request.
      if (dialog !== currentDialog) dialog.close();
    };
    this.#finishConsent = done;
    this.#saveTimer = setTimeout(() => done({ error: true }), 10000);
    try { this.#privacy.setTrackingConsent(consent, done); }
    catch { done({ error: true }); }
  }

  #onConsentChanged = () => {
    if (!this.#privacy) return;
    this.#clearUnconsentedStorage();
    if (!this.#trackingAccepted()) {
      if (this.#visit) { this.#visit.eligible = false; this.#saveVisit(); }
      this.#reminderRequest?.abort();
      this.#closeReminder();
    }
    // Shopify's event confirms the choice, including choices made in its native banner.
    if (this.#finishConsent) this.#finishConsent();
    else this.#closeCookies();
    this.#scheduleWelcome();
  };

  #closeCookies() {
    this.#cookieOpen = false;
    this.#cookieDraft = undefined;
    this.refs.cookieDialog?.close();
  }

  #clearUnconsentedStorage() {
    if (!this.#preferencesAllowed()) {
      try { localStorage.removeItem(DISMISSED_KEY); } catch { /* Optional storage. */ }
    }
  }

  #preferencesAllowed() {
    try { return this.#privacy?.preferencesProcessingAllowed?.() === true; }
    catch { return false; }
  }

  #rememberSubscriber() {
    this.#subscribed = true;
    this.#session(SUBSCRIBED_KEY, '1');
    try { localStorage.setItem(SUBSCRIBED_KEY, '1'); } catch { /* Session fallback. */ }
  }

  #isSubscriber() {
    if (this.#subscribed || this.dataset.subscribed === 'true' || this.#session(SUBSCRIBED_KEY) === '1') return true;
    try { return localStorage.getItem(SUBSCRIBED_KEY) === '1'; } catch { return false; }
  }

  #onSubscriptionChanged = (event) => {
    if (event.key !== SUBSCRIBED_KEY || event.newValue !== '1') return;
    this.#subscribed = true;
    if (this.#welcomeState.stage !== 'success') this.#dismissWelcome();
  };

  #trackingAccepted() {
    try {
      const consent = this.#privacy?.currentVisitorConsent();
      return PURPOSES.every((purpose) => consent?.[purpose] === 'yes') && this.#preferencesAllowed()
        && this.#privacy.analyticsProcessingAllowed?.() === true
        && this.#privacy.marketingAllowed?.() === true;
    } catch { return false; }
  }

  #startVisit() {
    if (this.dataset.cartReminderEnabled !== 'true' || this.#preview || window.Shopify?.designMode) return;
    const now = Date.now();
    try {
      const saved = JSON.parse(this.#session(VISIT_KEY) || 'null');
      if (!this.#visit) this.#visit = saved;
    } catch { this.#visit = undefined; }
    if (!this.#visit || !Number.isFinite(this.#visit.lastSeen) || now - this.#visit.lastSeen >= VISIT_TIMEOUT) {
      this.#visit = {
        startedAt: now, lastSeen: now,
        eligible: this.#trackingAccepted() && Number(this.dataset.cartCount) > 0,
        reminded: false,
      };
      this.#reminderItems = undefined;
    }
    this.#visit.lastSeen = now;
    if (!this.#saveVisit()) this.#visit.eligible = false;
  }

  #saveVisit() {
    return this.#visit && this.#session(VISIT_KEY, JSON.stringify(this.#visit)) !== null;
  }

  #touchVisit = () => {
    if (!this.#visit || Date.now() - this.#visit.lastSeen < 1000) return;
    this.#visit.lastSeen = Date.now();
    this.#saveVisit();
  };

  #onVisibilityChanged = () => {
    if (document.hidden) this.#touchVisit();
    else {
      this.#startVisit();
      this.#scheduleWelcome();
    }
  };

  #scheduleReminder() {
    if (!this.refs.cartReminderDialog || !this.#visit?.eligible || this.#visit.reminded || !this.#trackingAccepted()) return false;
    const remaining = this.#visit.startedAt + 5000 - Date.now();
    if (remaining > 0 || this.#welcomeBlocked()) {
      this.#timer = setTimeout(this.#scheduleWelcome, remaining > 0 ? remaining : 250);
    } else if (this.#reminderItems) {
      const target = this.refs.cartReminderDialog.querySelector('[data-cart-reminder-items]');
      if (!target) return false;
      target.replaceWith(this.#reminderItems);
      this.#reminderItems = undefined;
      this.#visit.reminded = true;
      this.#saveVisit();
      this.#seen = true;
      this.#session(SESSION_KEY, '1');
      this.#session(DEADLINE_KEY, '');
      this.#reminderOpen = true;
      this.#show(this.refs.cartReminderDialog);
      this.refs.cartReminderDialog.querySelector('h2')?.focus({ preventScroll: true });
    } else if (!this.#reminderLoading) {
      this.#loadReminder();
    }
    return true;
  }

  async #loadReminder() {
    this.#reminderLoading = true;
    const request = new AbortController();
    this.#reminderRequest = request;
    const lifecycle = this.#abort.signal;
    const abort = () => request.abort();
    lifecycle.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 8000);
    try {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = html.querySelector('[data-cart-reminder-items]');
      if (request.signal.aborted || !this.#visit.eligible || !this.#trackingAccepted()) return;
      if (Number(items?.dataset.cartCount) > 0) this.#reminderItems = items;
      else { this.#visit.eligible = false; this.#saveVisit(); }
    } catch {
      if (lifecycle.aborted) return;
      this.#visit.eligible = false;
      this.#saveVisit();
    } finally {
      clearTimeout(timeout);
      lifecycle.removeEventListener('abort', abort);
      if (this.#reminderRequest === request) {
        this.#reminderLoading = false;
        if (this.isConnected && !this.#abort.signal.aborted) this.#scheduleWelcome();
      }
    }
  }

  #closeReminder() {
    this.#reminderOpen = false;
    this.refs.cartReminderDialog?.close();
  }

  #session(key, value) {
    try {
      if (value === '') sessionStorage.removeItem(key);
      else if (value !== undefined) sessionStorage.setItem(key, value);
      return sessionStorage.getItem(key);
    } catch { return null; }
  }
}

if (!customElements.get('consent-welcome')) customElements.define('consent-welcome', ConsentWelcome);


